import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { buildThumbnails, extractVideoId } from "../lib/youtube";
import { ThumbnailCard } from "./ThumbnailCard";

/**
 * Paste a YouTube URL → grid of every thumbnail resolution. `highlight` outlines
 * one resolution (used by the HD variant page).
 */
export function ThumbnailGrabber({ highlight }: { highlight?: string }) {
  const [value, setValue] = useState("");
  const id = useMemo(() => extractVideoId(value), [value]);
  const thumbs = useMemo(() => (id ? buildThumbnails(id) : []), [id]);
  const showError = value.trim().length > 0 && !id;

  return (
    <div className="pct-card space-y-6 rounded-2xl p-6 sm:p-8">
      <div className="space-y-1.5">
        <Label htmlFor="yt-url" className="text-muted-foreground">
          {m.yt_input_label()}
        </Label>
        <Input
          id="yt-url"
          type="text"
          inputMode="url"
          autoComplete="off"
          spellCheck={false}
          placeholder={m.yt_input_placeholder()}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="border-black/15 bg-white text-neutral-900 placeholder:text-neutral-500 dark:border-black/15 dark:bg-white dark:text-neutral-900"
        />
        {showError && (
          <p className="text-sm text-destructive" aria-live="polite">
            {m.yt_invalid()}
          </p>
        )}
      </div>

      {id && thumbs.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {thumbs.map((t) => (
            <ThumbnailCard
              key={t.key}
              id={id}
              thumb={t}
              highlighted={t.key === highlight}
            />
          ))}
        </div>
      ) : (
        !showError && (
          <p className="text-sm text-muted-foreground">{m.yt_hint()}</p>
        )
      )}
    </div>
  );
}
